import {
  BlockStack,
  Box,
  Card,
  ContextualSaveBar,
  Form,
  Layout,
  Loading,
  Page,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import { lengthLessThan, lengthMoreThan, notEmpty, useField, useForm } from '@shopify/react-form';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

import DialogConfirm from 'components/dialogConfirm';
import { FormPrompt } from 'components/FormPrompt';
import QuickUploadImage from 'components/oneclick-upload-image';
import TextFieldRichText from 'components/TextFieldRichText';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import helpers from 'helpers';
import __ from 'languages/index';
import { useCreatePost, useDeletePost, useMutateGetPost, useUpdatePost } from 'queries/posts.query';

export default function PagesEdit() {
  const navigate = useNavigate();
  const { pages_slug } = useParams();
  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();

  const { mutate: getEntity, data: entity, isPending: loading } = useMutateGetPost();
  const { mutateAsync: createEntity, isPending: creating } = useCreatePost();
  const { mutateAsync: updateEntity, isPending: updating } = useUpdatePost();
  const { mutateAsync: deleteEntity, isPending: deleting } = useDeletePost();

  const isEditing = Boolean(pages_slug);
  const [showConfirmUnsavedForm, setShowConfirmUnsavedForm] = useState(false);
  const [uploadError, setUploadError] = useState<string>(null);

  useEffect(() => {
    if (pages_slug) getEntity(pages_slug);
  }, [getEntity, pages_slug]);

  const pageFields = {
    post_title: useField<string>({
      value: entity?.post_title ?? '',
      validates: [
        notEmpty('Trường này không được để trống.'),
        lengthLessThan(250, 'Không được dài quá 250 ký tự.'),
        lengthMoreThan(2, 'Không được ngắn hơn 2 ký tự.'),
      ],
    }),
    post_name: useField<string>({
      value: entity?.post_name ?? '',
      validates: [
        notEmpty('Trường này không được để trống.'),
        lengthLessThan(250, 'Không được dài quá 250 ký tự.'),
        lengthMoreThan(2, 'Không được ngắn hơn 2 ký tự.'),
        (value) => (helpers.isUTF8(value) ? 'Không được sử dụng ký tự UTF-8 ở đây.' : undefined),
      ],
    }),
    post_excerpt: useField<string>({
      value: entity?.post_excerpt ?? '',
      validates: [lengthLessThan(255, 'Không được dài quá 255 ký tự.')],
    }),
    post_content: useField<string>({
      value: entity?.post_content ?? '',
      validates: [lengthLessThan(10000, 'Không được dài quá 10.000 ký tự.')],
    }),
    post_status: useField<string>({
      value: entity?.post_status ?? 'draft',
      validates: [],
    }),
    post_thumbnail: useField<string>({
      value: entity?.post_thumbnail ?? '',
      validates: [],
    }),
    post_thumbnail_cache: useField<string>({
      value: entity?.post_thumbnail_to_media?.media_thumbnail?.['128x128'] ?? entity?.post_thumbnail_to_media?.media_url ?? '',
      validates: [],
    }),
  };

  useEffect(() => {
    if (isEditing) return;
    pageFields.post_name.onChange(helpers.slugify(pageFields.post_title.value));
  }, [pageFields.post_title.value, isEditing]);

  const { fields, submit, dirty } = useForm({
    fields: pageFields,
    async onSubmit(values) {
      try {
        const payload = {
          post_title: values.post_title,
          post_name: values.post_name,
          post_excerpt: values.post_excerpt,
          post_content: values.post_content,
          post_status: values.post_status,
          post_thumbnail: values.post_thumbnail,
          post_type: 'page',
          comment_status: 0,
        };

        if (isEditing) {
          await updateEntity({ post_id: entity.post_id, ...payload });
          addNotification('info', __('updated_successfully'));
        } else {
          const createdPage = await createEntity(payload);
          addNotification('info', __('created_successfully'));
          if (createdPage?.post_id) navigate(`/pages/edit/${createdPage.post_id}`, { replace: true });
          else navigate('/pages', { replace: true });
        }

        return { status: 'success' as const };
      } catch (error: any) {
        if (error?.params?.field) pageFields[error.params.field]?.setError(error.message);
        return { status: 'fail' as const, errors: [] };
      }
    },
  });

  const leaveEditor = useCallback(() => {
    if (dirty) setShowConfirmUnsavedForm(true);
    else navigate('/pages');
  }, [dirty, navigate]);

  const handleDeletePage = useCallback(async () => {
    if (!pages_slug) return;
    const confirmed = await showDeleteConfirm('Xóa trang này?', 'Bạn có chắc muốn xóa không? Hành động này không thể khôi phục.');
    if (!confirmed) return;

    await deleteEntity(pages_slug);
    addNotification('info', __('deleted_successfully'));
    navigate('/pages');
  }, [addNotification, deleteEntity, navigate, pages_slug, showDeleteConfirm]);

  const saving = creating || updating;

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Chỉnh sửa trang' : 'Tạo trang'}</title>
      </Helmet>
      {loading && <Loading />}
      {dirty && (
        <ContextualSaveBar
          alignContentFlush
          message="Thay đổi chưa được lưu"
          saveAction={{ content: 'Lưu lại', loading: saving, onAction: submit }}
          discardAction={{ content: 'Quay về', onAction: leaveEditor }}
        />
      )}

      <Page
        title={isEditing ? 'Chỉnh sửa trang' : 'Tạo trang'}
        backAction={{ content: 'Danh sách trang', onAction: leaveEditor }}
        primaryAction={{ content: 'Lưu', onAction: submit, disabled: !dirty, loading: saving }}
        secondaryActions={
          isEditing
            ? [{ content: 'Xóa', destructive: true, loading: deleting, onAction: handleDeletePage }]
            : undefined
        }
      >
        <FormPrompt hasUnsavedChanges={dirty} />
        <DialogConfirm
          tone="critical"
          show={showConfirmUnsavedForm}
          onClose={(confirmed) => {
            if (confirmed) navigate('/pages');
            setShowConfirmUnsavedForm(false);
          }}
          title="Rời khỏi trang mà chưa lưu thay đổi?"
          content="Rời khỏi trang này sẽ xóa tất cả các thay đổi chưa được lưu. Bấm hủy bỏ để tiếp tục chỉnh sửa."
        />

        <Layout>
          <Layout.Section>
            <Form onSubmit={submit}>
              <Card>
                <BlockStack gap="400">
                  <TextField autoComplete="off" label="Tiêu đề trang" autoFocus {...fields.post_title} />
                  <TextField autoComplete="off" label="Đường dẫn tĩnh của trang" {...fields.post_name} />
                  <TextField
                    autoComplete="off"
                    showCharacterCount
                    maxLength={255}
                    label="Mô tả ngắn"
                    multiline={2}
                    {...fields.post_excerpt}
                  />
                  <TextFieldRichText
                    key={entity?.post_id ?? 'new-page'}
                    label="Nội dung"
                    initialData={entity?.post_content}
                    onCallback={fields.post_content.onChange}
                  />
                </BlockStack>
              </Card>
            </Form>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <Box background="bg-fill" borderRadius="400" padding="400">
              <BlockStack gap="400">
                <Text as="h4" variant="headingSm">
                  Ảnh đại diện
                </Text>
                <QuickUploadImage
                  height={250}
                  onSuccess={(media) => {
                    fields.post_thumbnail.onChange(media.media_id);
                    fields.post_thumbnail_cache.onChange(media.media_url);
                  }}
                  onError={setUploadError}
                  title=""
                  placeholder={helpers.getMediaLink(fields.post_thumbnail_cache.value)}
                />
                <Text as="p" tone="subdued" variant="bodyXs">
                  Ảnh nên có tỉ lệ Full HD hoặc kích thước 256x169 pixel.
                </Text>
                {uploadError && (
                  <Text as="p" tone="critical">
                    {uploadError}
                  </Text>
                )}
              </BlockStack>
            </Box>

            <br />
            <Card>
              <Select
                label="Trạng thái trang"
                options={[
                  { label: __('post_status_draft'), value: 'draft' },
                  { label: __('post_status_publish'), value: 'publish' },
                  { label: __('post_status_deleted'), value: 'deleted' },
                  { label: __('post_status_lock'), value: 'lock' },
                ]}
                value={fields.post_status.value}
                onChange={fields.post_status.onChange}
              />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
      <br />
      <br />
    </>
  );
}
